import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { 
  getUsers, 
  deleteUser, 
  suspendUser, 
  banUser, 
  activateUser,
  bulkUpdateUserStatus,
  bulkSendEmail
} from "../../../data";
import { AdminLayout } from "../shared/layout";
import { useNavigate } from "react-router-dom";
import { 
  Eye, 
  Pencil, 
  Trash2, 
  UserPlus, 
  UserX, 
  Ban, 
  CheckCircle,
  Clock
} from "lucide-react";
import { DataTable } from "@/components/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/components/ui/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Users as UsersIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreVertical } from "lucide-react";

export function UserListScreen() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedUser, setSelectedUser] = useState(null);
  const [actionDialog, setActionDialog] = useState({ open: false, type: null });
  const [reason, setReason] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkDialog, setBulkDialog] = useState({ open: false, type: null });
  const [bulkEmailData, setBulkEmailData] = useState({ subject: "", message: "" });

  const { data, isLoading } = useQuery({
    queryKey: ["users"],
    queryFn: getUsers,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
      toast({
        title: "Başarılı",
        description: "Kullanıcı başarıyla silindi",
        variant: "success",
      });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: "Kullanıcı silinirken bir hata oluştu",
        variant: "destructive",
      });
      console.error("Kullanıcı silme hatası:", error);
    },
  });

  const suspendMutation = useMutation({
    mutationFn: ({ id, reason }) => suspendUser(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
      setActionDialog({ open: false, type: null });
      setReason("");
      toast({
        title: "Başarılı",
        description: "Kullanıcı askıya alındı",
        variant: "success",
      });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: "İşlem sırasında bir hata oluştu",
        variant: "destructive",
      });
    },
  });

  const banMutation = useMutation({
    mutationFn: ({ id, reason }) => banUser(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
      setActionDialog({ open: false, type: null });
      setReason("");
      toast({
        title: "Başarılı",
        description: "Kullanıcı yasaklandı",
        variant: "success",
      });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: "İşlem sırasında bir hata oluştu",
        variant: "destructive",
      });
    },
  });

  const activateMutation = useMutation({
    mutationFn: activateUser,
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
      toast({
        title: "Başarılı",
        description: "Kullanıcı aktifleştirildi",
        variant: "success",
      });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: "İşlem sırasında bir hata oluştu",
        variant: "destructive",
      });
    },
  });

  const handleDelete = (id) => {
    deleteMutation.mutate(id);
  };

  const handleSuspend = (user) => {
    setSelectedUser(user);
    setActionDialog({ open: true, type: "suspend" });
  };

  const handleBan = (user) => {
    setSelectedUser(user);
    setActionDialog({ open: true, type: "ban" });
  };

  const handleActivate = (user) => {
    activateMutation.mutate(user.id);
  };

  const handleConfirmAction = () => {
    if (actionDialog.type === "suspend") {
      suspendMutation.mutate({ id: selectedUser.id, reason });
    } else if (actionDialog.type === "ban") {
      banMutation.mutate({ id: selectedUser.id, reason });
    }
  };

  // Bulk operations mutations
  const bulkStatusMutation = useMutation({
    mutationFn: ({ userIds, status, reason }) => bulkUpdateUserStatus(userIds, status, reason),
    onSuccess: () => {
      queryClient.invalidateQueries(["users"]);
      setBulkDialog({ open: false, type: null });
      setSelectedUsers([]);
      setReason("");
      toast({
        title: "Başarılı",
        description: "Seçili kullanıcıların durumu güncellendi",
        variant: "success",
      });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: "Toplu işlem sırasında bir hata oluştu",
        variant: "destructive",
      });
    },
  });

  const bulkEmailMutation = useMutation({
    mutationFn: ({ userIds, subject, message }) => bulkSendEmail(userIds, subject, message),
    onSuccess: () => {
      setBulkDialog({ open: false, type: null });
      setSelectedUsers([]);
      setBulkEmailData({ subject: "", message: "" });
      toast({
        title: "Başarılı",
        description: "Email gönderildi",
        variant: "success",
      });
    },
    onError: (error) => {
      toast({
        title: "Hata",
        description: "Email gönderilirken bir hata oluştu",
        variant: "destructive",
      });
    },
  });

  const handleBulkAction = (type) => {
    setBulkDialog({ open: true, type });
  };

  const handleConfirmBulkAction = () => {
    if (bulkDialog.type === "status") {
      bulkStatusMutation.mutate({ userIds: selectedUsers, status: "SUSPENDED", reason });
    } else if (bulkDialog.type === "email") {
      bulkEmailMutation.mutate({ 
        userIds: selectedUsers, 
        subject: bulkEmailData.subject, 
        message: bulkEmailData.message 
      });
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedUsers.length === data?.data?.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(data?.data?.map(u => u.id) || []);
    }
  };

  const actions = [
    {
      name: "Kullanıcı Ekle",
      icon: <UserPlus className="mr-2 h-4 w-4" />,
      onClick: () => navigate("/admin/users/new"),
    },
  ];

  const getRoleBadge = (roles) => {
    if (!roles || roles.length === 0) return <Badge variant="outline">Rol Yok</Badge>;
    
    const roleColors = {
      ADMIN: "red",
      USER: "blue",
      MODERATOR: "green",
    };

    return roles.map((roleObj, index) => (
      <Badge 
        key={index} 
        variant="outline" 
        className="mr-1"
        style={{ backgroundColor: roleColors[roleObj.role.name] || "gray", color: "white" }}
      >
        {roleObj.role.name}
      </Badge>
    ));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      ACTIVE: { variant: "success", label: "Aktif", icon: <CheckCircle className="h-3 w-3 mr-1" /> },
      INACTIVE: { variant: "secondary", label: "Pasif", icon: <Clock className="h-3 w-3 mr-1" /> },
      SUSPENDED: { variant: "warning", label: "Askıda", icon: <UserX className="h-3 w-3 mr-1" /> },
      BANNED: { variant: "destructive", label: "Yasaklı", icon: <Ban className="h-3 w-3 mr-1" /> },
    };

    const config = statusConfig[status] || statusConfig.INACTIVE;

    return (
      <Badge variant={config.variant} className="flex items-center w-fit">
        {config.icon}
        {config.label}
      </Badge>
    );
  };

  const columns = [
    { 
      key: "select", 
      label: (
        <Checkbox
          checked={selectedUsers.length === data?.data?.length && data?.data?.length > 0}
          onCheckedChange={toggleSelectAll}
        />
      ),
      render: (row) => (
        <Checkbox
          checked={selectedUsers.includes(row.id)}
          onCheckedChange={() => toggleUserSelection(row.id)}
        />
      )
    },
    { key: "name", label: "İsim" },
    { key: "email", label: "E-posta" },
    { 
      key: "status", 
      label: "Durum", 
      render: (row) => getStatusBadge(row.status || "INACTIVE")
    },
    { 
      key: "roles", 
      label: "Roller", 
      render: (row) => getRoleBadge(row.roles)
    },
    { 
      key: "platform", 
      label: "Platform", 
      render: (row) => (
        <span className="text-sm text-gray-600">
          {row.lastLoginPlatform || "N/A"}
        </span>
      )
    },
    { 
      key: "actions", 
      label: "İşlemler", 
      render: (row) => (
        <div className="flex space-x-2">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(`/admin/users/${row.id}`)}
            className="hover:bg-blue-100"
          >
            <Pencil className="h-4 w-4 text-blue-500" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>İşlemler</DropdownMenuLabel>
              <DropdownMenuSeparator />
              
              {row.status !== "ACTIVE" && (
                <DropdownMenuItem onClick={() => handleActivate(row)}>
                  <CheckCircle className="mr-2 h-4 w-4 text-green-500" />
                  Aktifleştir
                </DropdownMenuItem>
              )}
              
              {row.status !== "SUSPENDED" && row.status !== "BANNED" && (
                <DropdownMenuItem onClick={() => handleSuspend(row)}>
                  <UserX className="mr-2 h-4 w-4 text-yellow-500" />
                  Askıya Al
                </DropdownMenuItem>
              )}
              
              {row.status !== "BANNED" && (
                <DropdownMenuItem onClick={() => handleBan(row)}>
                  <Ban className="mr-2 h-4 w-4 text-red-500" />
                  Yasakla
                </DropdownMenuItem>
              )}
              
              <DropdownMenuSeparator />
              
              <DropdownMenuItem onClick={() => navigate(`/admin/users/${row.id}/history`)}>
                <Clock className="mr-2 h-4 w-4 text-gray-500" />
                Login Geçmişi
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="hover:bg-red-100"
              >
                <Trash2 className="h-4 w-4 text-red-500" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Kullanıcıyı Sil</AlertDialogTitle>
                <AlertDialogDescription>
                  Bu kullanıcıyı silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>İptal</AlertDialogCancel>
                <AlertDialogAction onClick={() => handleDelete(row.id)}>Sil</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )
    },
  ];

  return (
    <AdminLayout
      title="Kullanıcılar"
      description="Kullanıcılarınızı buradan yönetin."
      actions={actions}
      loading={isLoading}
    >
      {/* Bulk Actions Bar */}
      {selectedUsers.length > 0 && (
        <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <UsersIcon className="h-5 w-5 text-blue-600" />
            <span className="font-medium text-blue-900">
              {selectedUsers.length} kullanıcı seçildi
            </span>
          </div>
          <div className="flex space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleBulkAction("status")}
            >
              <UserX className="mr-2 h-4 w-4" />
              Durumu Değiştir
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleBulkAction("email")}
            >
              <Mail className="mr-2 h-4 w-4" />
              Toplu Email Gönder
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedUsers([])}
            >
              İptal
            </Button>
          </div>
        </div>
      )}

      {data && (
        <DataTable 
          data={data.data} 
          title="Kullanıcı Listesi"
          columns={columns}
          enableSearch={true}
          enableSort={true}
          enablePagination={true}
          itemsPerPage={10}
        />
      )}

      {/* Suspend/Ban Dialog */}
      <Dialog 
        open={actionDialog.open} 
        onOpenChange={(open) => {
          setActionDialog({ open, type: actionDialog.type });
          if (!open) setReason("");
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {actionDialog.type === "suspend" ? "Kullanıcıyı Askıya Al" : "Kullanıcıyı Yasakla"}
            </DialogTitle>
            <DialogDescription>
              {actionDialog.type === "suspend" 
                ? "Bu kullanıcıyı askıya almak istediğinizden emin misiniz? Askıya alınan kullanıcılar sisteme giriş yapamaz." 
                : "Bu kullanıcıyı yasaklamak istediğinizden emin misiniz? Yasaklanan kullanıcılar sisteme giriş yapamaz ve tüm işlemleri engellenecektir."}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reason">Sebep (İsteğe bağlı)</Label>
              <Input
                id="reason"
                placeholder="İşlem sebebini girin..."
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
            
            {selectedUser && (
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="text-sm text-gray-600">
                  <span className="font-medium">Kullanıcı:</span> {selectedUser.name || selectedUser.email}
                </p>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">E-posta:</span> {selectedUser.email}
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setActionDialog({ open: false, type: null });
                setReason("");
              }}
            >
              İptal
            </Button>
            <Button
              variant={actionDialog.type === "ban" ? "destructive" : "default"}
              onClick={handleConfirmAction}
              disabled={suspendMutation.isLoading || banMutation.isLoading}
            >
              {suspendMutation.isLoading || banMutation.isLoading ? "İşleniyor..." : "Onayla"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Operations Dialog */}
      <Dialog 
        open={bulkDialog.open} 
        onOpenChange={(open) => {
          setBulkDialog({ open, type: bulkDialog.type });
          if (!open) {
            setReason("");
            setBulkEmailData({ subject: "", message: "" });
          }
        }}
      >
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {bulkDialog.type === "status" 
                ? "Toplu Durum Değişikliği" 
                : "Toplu Email Gönder"}
            </DialogTitle>
            <DialogDescription>
              {bulkDialog.type === "status"
                ? `${selectedUsers.length} kullanıcının durumunu değiştirmek üzeresiniz.`
                : `${selectedUsers.length} kullanıcıya email göndereceksiniz.`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {bulkDialog.type === "status" ? (
              <>
                <div className="space-y-2">
                  <Label>Yeni Durum</Label>
                  <div className="text-sm text-gray-600">
                    Tüm seçili kullanıcılar "SUSPENDED" (Askıda) durumuna alınacak.
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bulk-reason">Sebep (İsteğe bağlı)</Label>
                  <Input
                    id="bulk-reason"
                    placeholder="İşlem sebebini girin..."
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="email-subject">Konu *</Label>
                  <Input
                    id="email-subject"
                    placeholder="Email konusu..."
                    value={bulkEmailData.subject}
                    onChange={(e) => setBulkEmailData({ ...bulkEmailData, subject: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email-message">Mesaj *</Label>
                  <Textarea
                    id="email-message"
                    placeholder="Email içeriği..."
                    value={bulkEmailData.message}
                    onChange={(e) => setBulkEmailData({ ...bulkEmailData, message: e.target.value })}
                    rows={6}
                  />
                </div>
              </>
            )}
            
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-sm text-gray-600">
                <span className="font-medium">Seçili kullanıcı sayısı:</span> {selectedUsers.length}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setBulkDialog({ open: false, type: null });
                setReason("");
                setBulkEmailData({ subject: "", message: "" });
              }}
            >
              İptal
            </Button>
            <Button
              onClick={handleConfirmBulkAction}
              disabled={
                bulkStatusMutation.isLoading || 
                bulkEmailMutation.isLoading ||
                (bulkDialog.type === "email" && (!bulkEmailData.subject || !bulkEmailData.message))
              }
            >
              {bulkStatusMutation.isLoading || bulkEmailMutation.isLoading 
                ? "İşleniyor..." 
                : "Onayla"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}